import { useCallback, useContext } from 'react';
import { GlobalConfigurationContext } from '../../../lib/context/GlobalConfigurationContext';
import { ContentPagesCacheContext } from '../../../lib/context/ContentPagesCacheContext';
import { RoomAssetsContext } from '../../../lib/context/RoomAssetsContext';
import RequestConfigurationFactory from '../../../Services/Request/RequestConfigurationFactory';

interface AssetDetails {
  id: string;
  assetDetails: {
    file: {
      media: {
        totalPages: number;
      };
    };
  };
}

interface GetAssetResponse {
  data: {
    externalSharingUnauthenticated: {
      getExternalSharingDetails: {
        assetDetails: AssetDetails[];
      };
    };
  };
}

const useContentPages = (): {
  fetchMediaPages: (sharableId: string, assetId: string) => Promise<number>;
} => {
  const { userAuth } = useContext(GlobalConfigurationContext);
  const { cache, setCache } = useContext(ContentPagesCacheContext);
  const { updateAsset } = useContext(RoomAssetsContext);
  const equipExternalUrl = RequestConfigurationFactory.getEquipExternalUrl();

  const fetchMediaPages = useCallback(
    async (sharableId: string, assetId: string): Promise<number> => {
      if (cache[assetId] >= 0) {
        return cache[assetId];
      }

      try {
        const response = await fetch(equipExternalUrl, {
          headers: {
            'x-token': sharableId,
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          method: 'POST',
          body: JSON.stringify({
            query: `query GetInteractionDetailsByIdQuery {
              externalSharingUnauthenticated {
                getExternalSharingDetails {
                  interactionId
                  assetDetails {
                    id
                    assetDetails {
                      __typename
                      ... on ExternalRefAsset {
                        id
                        file {
                          __typename
                          ... on ExternalFile {
                            id
                            media {
                              __typename
                              ... on DocMedia {
                                totalPages
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }`,
          }),
        });
        const data: GetAssetResponse = await response.json();
        const assetDetailsArray =
          data.data.externalSharingUnauthenticated.getExternalSharingDetails
            .assetDetails;
        const asset = assetDetailsArray.find(({ id }) => id === assetId);
        const totalPages = asset?.assetDetails?.file?.media?.totalPages;
        setCache(assetId, totalPages || 0);
        updateAsset(asset || {});
        return totalPages || 0;
      } catch (error) {
        console.error('Error fetching media pages:', error);
        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userAuth, cache]
  );

  return { fetchMediaPages };
};

export default useContentPages;
